import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { STATS } from "@/lib/siteData";

function extractNumber(v) {
    const m = String(v).match(/(\d[\d,.]*)/);
    if (!m) return { num: 0, suffix: v };
    const numStr = m[1].replace(/,/g, "");
    const num = parseFloat(numStr);
    const suffix = String(v).replace(m[1], "");
    return { num, suffix };
}

function Counter({ value, active }) {
    const { num, suffix } = extractNumber(value);
    const [n, setN] = useState(0);
    useEffect(() => {
        if (!active) return;
        const start = performance.now();
        const dur = 1400;
        let raf;
        const tick = (t) => {
            const p = Math.min(1, (t - start) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            setN(num * eased);
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [active, num]);
    const display =
        num >= 1000 ? Math.round(n).toLocaleString() : Math.round(n).toString();
    return (
        <span>
            {display}
            {suffix}
        </span>
    );
}

export default function Stats() {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-100px" });

    return (
        <section
            data-testid="stats"
            ref={ref}
            className="relative py-20 md:py-24 border-y hairline"
        >
            <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-2 md:grid-cols-4 gap-8">
                {STATS.map((s, i) => (
                    <motion.div
                        key={s.label}
                        data-testid={`stat-${i}`}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: i * 0.07 }}
                    >
                        <div className="font-display text-5xl md:text-6xl tracking-tight">
                            <Counter value={s.value} active={inView} />
                        </div>
                        <div className="mt-2 text-xs md:text-sm uppercase tracking-[0.24em] text-muted-foreground">
                            {s.label}
                        </div>
                    </motion.div>
                ))}
            </div>
        </section>
    );
}
